import React from 'react';
import { IAppError, TFileId } from 'types';
import { v4 as uuidv4 } from 'uuid';

export const capitalizeFirstLetter = (string: string) => {
	return string.charAt(0).toUpperCase() + string.slice(1);
};

export const trueWordEnding = (num: number, singular: string, plural: string) => {
	const str = String(num);
	if (str.slice(-1) === '1') {
		return singular;
	}
	return plural;
};

export const capitalizeFirstWord = (string: string) => {
	if (!string) {
		return '';
	}
	const words = string.match(/[A-Za-z][a-z]*/g) || [];
	return words.map((el, i) => (i === 0 ? capitalizeFirstLetter(el) : el.toLowerCase())).join(' ');
};

export const rgbToHex = (rgb: string) => {
	const matchedRGB = rgb.match(/^rgba?[\s+]?\([\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?,[\s+]?(\d+)[\s+]?/i);
	const hex =
		matchedRGB && matchedRGB.length === 4
			? '#' +
			  ('0' + parseInt(matchedRGB[1], 10).toString(16)).slice(-2) +
			  ('0' + parseInt(matchedRGB[2], 10).toString(16)).slice(-2) +
			  ('0' + parseInt(matchedRGB[3], 10).toString(16)).slice(-2)
			: '';
	return hex;
};

export const hexToRgb = (hex: string) => {
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
	const rgb = result
		? {
				r: parseInt(result[1], 16),
				g: parseInt(result[2], 16),
				b: parseInt(result[3], 16),
		  }
		: null;
	return rgb;
};

export const createAppError = (error: Error, retryHandler?: () => void) => {
	const newError: IAppError = {
		id: uuidv4(),
		retryHandler,
		message: error.message || 'unknownError',
		name: error.name || 'noErrorData',
		stack: error.stack || 'noErrorData',
	};
	return newError;
};

export const createAppErrorMessage = (error: unknown, message: string) => {
	return error instanceof Error ? `${error.message}.\n${message}` : 'unknownError';
};

// Replaces nullable values instead of undefined for sending valid data to BE (relevant for select components)
export const convertUndefinedFields = <T extends object>(dataForSubmit: T) => {
	const convertedData: T = { ...dataForSubmit };
	const entries = Object.entries(dataForSubmit) as [string, (typeof dataForSubmit)[keyof T]][];
	entries.forEach(
		([key, value]) =>
			value === undefined && (convertedData[key as keyof T] = null as unknown as (typeof dataForSubmit)[keyof T]),
	);

	return convertedData;
};

export const addingIdChunkToEndOfName = (id: string | null, name: string | null) => {
	return id && name ? name + ' / ...' + id.slice(-4) : name || '';
};

export const preventDefaultForEnterKey = (e: React.KeyboardEvent) => {
	e.key === 'Enter' && e.preventDefault();
};

export const addIdMapper = <T>(entity: T) => {
	return {
		id: uuidv4() as string,
		...entity,
	};
};

export const switchArrayItems = <ArrayType>(array: ArrayType[], from: number, to: number): ArrayType[] => {
	const arrayCopy = [...array];
	arrayCopy.splice(to, 0, arrayCopy.splice(from, 1)[0]);
	return arrayCopy;
};

export const getFileIdForSubmit = ({ fileId, filePath }: { fileId: TFileId; filePath: Nullable<string> }): TFileId => {
	// If file not updated
	if (!fileId && filePath) {
		return '';
	}
	// If file deleted
	if (!filePath && !fileId) {
		return null;
	}
	// If new file uploaded
	return fileId;
};

export const getS3FilePath = (preSignedUrl: string): string => {
	const url = new URL(preSignedUrl);
	const pathname = url.pathname.replace(/^\/+/, '').replace(/-(small|medium)(?=\.[^.]+$)/, '');
	return pathname;
};
